/**
 * @param {unknown} selection - FormComponent autocomplete multiple value
 * @returns {number[]}
 */
export function getSelectedValuePropIDs(selection) {
    if (!Array.isArray(selection)) {
        return [];
    }

    return selection.map((item) => Number(item?.value ?? item)).filter((id) => Number.isFinite(id) && id > 0);
}

/**
 * Map assigned value-prop API rows (or similar) into FormComponent autocomplete options.
 *
 * @param {Array<{ id?: number, displayName?: string, text?: string, value?: number }>|undefined} valueProps
 * @returns {Array<{ text: string, value: number }>}
 */
export function mapAssignedValuePropsToFormOptions(valueProps) {
    if (!Array.isArray(valueProps)) {
        return [];
    }

    return valueProps
        .map((item) => {
            if (item == null) {
                return null;
            }
            const value = Number(item.id ?? item.value);
            if (!Number.isFinite(value) || value <= 0) {
                return null;
            }
            return {
                text: item.displayName ?? item.text ?? '',
                value,
            };
        })
        .filter(Boolean);
}

/**
 * Which external SKU to load value props from when hydrating Create Item Master.
 * Callers must choose the source intentionally:
 * - Duplicate open: pass initialExternalSku only (not on every externalSku change).
 * - Existing SKU select: pass selectedExternalSku only (never re-fall back to initial).
 *
 * @param {Object} args
 * @param {unknown} args.selectedExternalSku
 * @param {unknown} args.initialExternalSku
 * @returns {number|null}
 */
export function resolveExternalSkuForValuePropHydration({ initialExternalSku, selectedExternalSku }) {
    const selected = Number(selectedExternalSku);
    if (Number.isFinite(selected) && selected > 0) {
        return selected;
    }

    const fromInitial = Number(initialExternalSku);
    if (Number.isFinite(fromInitial) && fromInitial > 0) {
        return fromInitial;
    }

    return null;
}

/**
 * Value props are II-level. When creating a variant against an existing external SKU,
 * the field is disabled — do not re-POST the already-persisted selections onto that shared II.
 * Duplicate / new II still attaches after create.
 *
 * @param {Object} args
 * @param {boolean} args.isExistingItemInformation
 * @returns {boolean}
 */
export function shouldAttachValuePropsAfterCreate({ isExistingItemInformation }) {
    return !isExistingItemInformation;
}

/**
 * Whether Create Item Master submit should reset the form and call onSuccess.
 * Create can succeed while value-prop attach fails — that must not complete submit,
 * or the duplicate modal closes and the attach error never stays visible.
 *
 * @param {Object} args
 * @param {boolean} args.itemMasterCreated
 * @param {boolean} args.valuePropsAttachAttempted
 * @param {boolean} args.valuePropsAttachSucceeded
 * @returns {boolean}
 */
export function shouldCompleteCreateItemMasterSubmit({
    itemMasterCreated,
    valuePropsAttachAttempted,
    valuePropsAttachSucceeded,
}) {
    if (!itemMasterCreated) {
        return false;
    }
    if (!valuePropsAttachAttempted) {
        return true;
    }
    return valuePropsAttachSucceeded;
}

/**
 * When create succeeded but value-prop attach failed, the form stays open for retry.
 * The item master is already persisted — retry must skip create and only re-attach.
 *
 * @param {Object} args
 * @param {{ externalSku?: number|string|null, failedValuePropIDs?: number[] }|null|undefined} args.pendingValuePropAttach
 * @returns {boolean}
 */
export function shouldSkipItemMasterCreate({ pendingValuePropAttach }) {
    return pendingValuePropAttach != null;
}

/**
 * External SKU remembered after a successful create for a value-prop attach retry.
 *
 * @param {{ externalSku?: number|string|null, failedValuePropIDs?: number[] }|null|undefined} pendingValuePropAttach
 * @returns {number|null}
 */
export function getPendingAttachExternalSku(pendingValuePropAttach) {
    if (pendingValuePropAttach == null) {
        return null;
    }
    const sku = Number(pendingValuePropAttach.externalSku);
    return Number.isFinite(sku) && sku > 0 ? sku : null;
}

/**
 * Value-prop IDs whose attach response failed (aligned by index with attempted IDs).
 *
 * @param {Object} args
 * @param {number[]} args.attemptedValuePropIDs
 * @param {Array<{ status?: boolean }|null|undefined>} args.responses
 * @returns {number[]}
 */
export function getFailedValuePropIDs({ attemptedValuePropIDs, responses }) {
    if (!Array.isArray(attemptedValuePropIDs) || !Array.isArray(responses)) {
        return [];
    }

    return attemptedValuePropIDs.filter((id, index) => {
        if (!Number.isFinite(id) || id <= 0) {
            return false;
        }
        return !responses[index]?.status;
    });
}

/**
 * @param {unknown} ids
 * @returns {number[]}
 */
function normalizeValuePropIDs(ids) {
    if (!Array.isArray(ids)) {
        return [];
    }

    return ids.filter((id) => Number.isFinite(id) && id > 0);
}

/**
 * IDs already saved on a prior attach attempt: previously saved ∪ (attempted − failed).
 *
 * @param {Object} [args]
 * @param {number[]} [args.attemptedValuePropIDs]
 * @param {number[]} [args.failedValuePropIDs]
 * @param {number[]} [args.savedValuePropIDs]
 * @returns {number[]}
 */
export function getSavedValuePropIDs({ attemptedValuePropIDs, failedValuePropIDs, savedValuePropIDs } = {}) {
    const previouslySaved = normalizeValuePropIDs(savedValuePropIDs);
    const failedSet = new Set(normalizeValuePropIDs(failedValuePropIDs));
    const newlySaved = normalizeValuePropIDs(attemptedValuePropIDs).filter((id) => !failedSet.has(id));
    return [...new Set([...previouslySaved, ...newlySaved])];
}

/**
 * Which value-prop IDs to POST on this submit.
 * First attempt: the full selection. Retry: current selection minus IDs already saved
 * (selected − (originalAttempt − failed)), so removed failed props are not re-sent and
 * newly selected props are attached without duplicate-POSTing saved mappings.
 *
 * @param {Object} args
 * @param {number[]} args.selectedValuePropIDs
 * @param {{ attemptedValuePropIDs?: number[], failedValuePropIDs?: number[], savedValuePropIDs?: number[] }|null|undefined} args.pendingValuePropAttach
 * @returns {number[]}
 */
export function getValuePropIDsToAttach({ pendingValuePropAttach, selectedValuePropIDs }) {
    const selected = normalizeValuePropIDs(selectedValuePropIDs);
    if (pendingValuePropAttach == null) {
        return selected;
    }

    const savedSet = new Set(getSavedValuePropIDs(pendingValuePropAttach));
    return selected.filter((id) => !savedSet.has(id));
}

/**
 * Prefer the SKU sent on create or returned by create; otherwise find the newly created II
 * by internal name among rows that were not known before create.
 *
 * Duplicate keeps the source name, so name-matching must never attach onto a pre-existing
 * same-name SKU (including a later row that is not the duplicate source) when the new II
 * is missing from getItemInformation.
 *
 * @param {Object} args
 * @param {unknown} args.sentExternalSku
 * @param {unknown} [args.createdExternalSku] - SKU returned by createItemMaster response
 * @param {string} args.itemNameInternal
 * @param {Iterable<unknown>} [args.knownExternalSkus] - SKUs that existed before create
 * @param {Array<{ externalSku?: number, name?: string }>} args.itemInformationRows
 * @returns {number|null}
 */
export function resolveExternalSkuForValueProps({
    createdExternalSku = null,
    itemInformationRows = [],
    itemNameInternal,
    knownExternalSkus = [],
    sentExternalSku,
}) {
    const sent = Number(sentExternalSku);
    if (Number.isFinite(sent) && sent > 0) {
        return sent;
    }

    const created = Number(createdExternalSku);
    if (Number.isFinite(created) && created > 0) {
        return created;
    }

    const known = new Set(
        [...knownExternalSkus].map((sku) => Number(sku)).filter((sku) => Number.isFinite(sku) && sku > 0),
    );

    const candidates = itemInformationRows.filter((item) => {
        if (item?.name !== itemNameInternal) {
            return false;
        }
        const sku = Number(item?.externalSku);
        if (!Number.isFinite(sku) || sku <= 0) {
            return false;
        }
        if (known.has(sku)) {
            return false;
        }
        return true;
    });

    if (candidates.length === 0) {
        return null;
    }

    // Auto-assigned SKUs are allocated ascending — prefer the highest newly appeared match.
    const newest = candidates.reduce((best, item) =>
        Number(item.externalSku) > Number(best.externalSku) ? item : best,
    );
    const resolved = Number(newest.externalSku);
    return Number.isFinite(resolved) && resolved > 0 ? resolved : null;
}
